import React, { useMemo, useCallback, useEffect, useRef } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { useCalendarStore } from '../store/calendarStore';
import type { CalendarEvent, CalendarView, EdgeCalendar } from '../types/calendar.types';
import { useUserPreferencesStore } from '@/store/userPreferencesStore';
import { useAppStore } from '@/store/appStore';
import '../styles/fullcalendar.css';

interface FullCalendarViewProps {
  onEventClick?: (event: CalendarEvent) => void;
  onSlotSelect?: (slotInfo: { start: Date; end: Date; slots: Date[] }) => void;
  events?: CalendarEvent[]; // Optional prop to override store events (for matter-specific views)
  eventFilters?: any; // Optional prop to override store filters (for matter-specific filtering)
  containerMode?: boolean; // When true, adapts to fit within a container instead of full viewport
}

const FullCalendarView: React.FC<FullCalendarViewProps> = ({
  onEventClick,
  onSlotSelect,
  events: propEvents,
  eventFilters: propEventFilters,
  containerMode = false,
}) => {
  const { appPreferences } = useUserPreferencesStore();
  const calendarPrefs = appPreferences.calendar;
  const { isSidebarExpanded } = useAppStore(); // Main EdgeLex sidebar state
  const {
    calendars,
    events: storeEvents,
    visibleCalendarIds,
    currentView,
    currentDate,
    eventFilters: storeEventFilters,
    conflictingEvents,
    pendingChanges,
    isCalendarSidebarOpen,
    setCurrentView,
    // setCurrentDate, // Disabled - header controls navigation now
    setSelectedEventId,
    updateEvent,
    moveEvent,
  } = useCalendarStore();

  // Use prop events if provided (for matter-specific views), otherwise use store events
  const events = propEvents || storeEvents;
  const eventFilters = propEventFilters || storeEventFilters;

  const calendarRef = useRef<FullCalendar>(null);

  // Navigate to events when switching to Week/Day views
  useEffect(() => {
    if (calendarRef.current && (currentView === 'week' || currentView === 'day')) {
      const calendarApi = calendarRef.current.getApi();
      if (events && events.length > 0) {
        const firstEventDate = events[0].start;
        const eventDate = firstEventDate instanceof Date ? firstEventDate : new Date(firstEventDate);
        console.log(`🎯 [FullCalendarView] Navigating ${currentView} view to event date:`, eventDate.toISOString());
        calendarApi.gotoDate(eventDate);
      }
    }
  }, [currentView, events]);

  // Resize calendar when either sidebar toggles - FullCalendar needs to recalculate its size
  useEffect(() => {
    if (calendarRef.current) {
      // Wait for CSS transition to complete (main sidebar uses duration-300 = 300ms)
      const timeoutId = setTimeout(() => {
        const calendarApi = calendarRef.current?.getApi();
        if (calendarApi) {
          console.log('📐 [FullCalendarView] Sidebar toggled, updating calendar size');
          calendarApi.updateSize();
        }
      }, 350);
      return () => clearTimeout(timeoutId);
    }
  }, [isCalendarSidebarOpen, isSidebarExpanded]);

  // 🔍 Add comprehensive debug logging
  useEffect(() => {
    console.log('🔍 [FullCalendarView] Debug State Update:', {
      calendars: calendars?.length || 0,
      calendarsData: calendars?.map(cal => ({ id: cal.id, name: cal.name, isVisible: cal.isVisible })) || [],
      events: events?.length || 0,
      eventsData: events?.slice(0, 3).map(evt => ({ id: evt.id, title: evt.title, calendarId: evt.calendarId, start: evt.start })) || [],
      visibleCalendarIds: visibleCalendarIds || [],
      currentView,
      currentDate: currentDate?.toISOString() || 'no date',
      eventFilters,
    });
  }, [calendars, events, visibleCalendarIds, currentView, eventFilters]);

  // Convert CalendarEvent to FullCalendar EventInput format
  const fcEvents = useMemo(() => {
    console.log('🏭 [FullCalendarView] Processing events for FullCalendar...');
    console.log('📊 Raw data:', {
      totalEvents: events?.length || 0,
      totalCalendars: calendars?.length || 0,
      visibleCalendarIds: visibleCalendarIds || [],
      currentView, // Add current view to debug output
    });

    if (!events || !Array.isArray(events)) {
      console.warn('⚠️ [FullCalendarView] No events array found');
      return [];
    }

    if (!visibleCalendarIds || visibleCalendarIds.length === 0) {
      console.warn('⚠️ [FullCalendarView] No visible calendars - all events filtered out');
      return [];
    }

    const visibleEvents = events.filter((event: CalendarEvent) => {
      // Filter by calendar visibility
      if (!visibleCalendarIds.includes(event.calendarId) || event.isDeleted) {
        console.log(`🚫 Event "${event.title}" filtered out - calendar ${event.calendarId} not visible or deleted`);
        return false;
      }
      
      // Filter by event type if filters are active
      if (eventFilters.eventTypes && eventFilters.eventTypes.length > 0) {
        const passes = eventFilters.eventTypes.includes(event.eventTypeId || 'meeting');
        if (!passes) {
          console.log(`🚫 Event "${event.title}" filtered out - event type filter`);
          return false;
        }
      }
      
      // Filter by priority if filters are active
      if (eventFilters.priorities && eventFilters.priorities.length > 0) {
        const passes = eventFilters.priorities.includes(event.priority || 'medium');
        if (!passes) {
          console.log(`🚫 Event "${event.title}" filtered out - priority filter`);
          return false;
        }
      }
      
      console.log(`✅ Event "${event.title}" will be shown`);
      return true;
    });

    console.log('🎯 FullCalendar Events Debug:', {
      totalEvents: events.length,
      visibleEvents: visibleEvents.length,
      visibleCalendarIds,
      currentView,
    });

    // Get calendar map for event coloring
    const calendarMap = (calendars || []).reduce((map, cal) => {
      map[cal.id] = cal;
      return map;
    }, {} as Record<string, EdgeCalendar>);

    // Filter out events with invalid dates first
    const validEvents = visibleEvents.filter((event: CalendarEvent) => {
      const startDate = event.start instanceof Date ? event.start : new Date(event.start);
      const endDate = event.end instanceof Date ? event.end : new Date(event.end);
      const isStartValid = !isNaN(startDate.getTime());
      const isEndValid = !isNaN(endDate.getTime());
      
      if (!isStartValid || !isEndValid) {
        console.error(`❌ Invalid dates for event "${event.title}", skipping:`, {
          originalStart: event.start,
          originalEnd: event.end,
          parsedStart: startDate,
          parsedEnd: endDate,
          startValid: isStartValid,
          endValid: isEndValid
        });
        return false;
      }
      return true;
    });

    // Transform to FullCalendar format
    const transformedEvents = validEvents.map((event: CalendarEvent) => {
      const calendar = calendarMap[event.calendarId];
      
      // Get conflict/pending state styling
      const isConflicted = conflictingEvents.includes(event.id);
      const isPending = pendingChanges.includes(event.id);

      // Normalize dates (we know they're valid from the filter above)
      const startDate = event.start instanceof Date ? event.start : new Date(event.start);
      const endDate = event.end instanceof Date ? event.end : new Date(event.end);

      // For Week/Day views, ensure proper time handling
      const isAllDay = event.allDay || false;
      
      const fcEvent = {
        id: event.id,
        title: event.title || 'Untitled Event',
        start: startDate,
        end: endDate,
        allDay: isAllDay,
        
        // Styling based on calendar and state
        backgroundColor: isConflicted 
          ? '#ef4444' 
          : isPending 
            ? '#f59e0b' 
            : calendar?.color || 'var(--brand-accent-primary)',
        borderColor: isConflicted 
          ? '#dc2626' 
          : isPending 
            ? '#d97706' 
            : calendar?.color || 'var(--brand-accent-primary-hover)',
        textColor: '#ffffff',
        
        // Pass through the original event for click handling
        extendedProps: {
          calendarEvent: event,
          priority: event.priority,
          location: event.location,
          description: event.description,
          isConflicted,
          isPending,
        },
        
        // CSS classes for additional styling
        classNames: [
          'fc-event-custom',
          event.priority ? `fc-event-priority-${event.priority}` : '',
          isConflicted ? 'fc-event-conflicted' : '',
          isPending ? 'fc-event-pending' : '',
          currentView === 'month' ? 'fc-event-month-view' : 'fc-event-time-view',
        ].filter(Boolean),
      };

      console.log(`🎨 Transformed event "${event.title}" for ${currentView} view:`, {
        id: fcEvent.id,
        start: fcEvent.start.toISOString(),
        end: fcEvent.end.toISOString(),
        allDay: fcEvent.allDay,
        backgroundColor: fcEvent.backgroundColor,
        calendarId: event.calendarId,
        currentView,
      });

      return fcEvent;
    });

    console.log(`✨ [FullCalendarView] Returning ${transformedEvents.length} valid events to FullCalendar for ${currentView} view`);
    return transformedEvents;
  }, [events, visibleCalendarIds, eventFilters, calendars, conflictingEvents, pendingChanges, currentView]);

  // Custom event content renderer
  const renderEventContent = useCallback((eventInfo: any) => {
    const { event } = eventInfo;
    const calendarEvent = event.extendedProps.calendarEvent;
    
    // Get priority icon
    const getPriorityIcon = (priority: string) => {
      switch (priority) {
        case 'urgent': return '🚨';
        case 'high': return '🔴';
        case 'medium': return '🟡';
        case 'low': return '🟢';
        default: return '';
      }
    };

    console.log('🎨 Rendering FullCalendar event:', event.title);

    return (
      <div className="fc-event-content-custom">
        <div className="fc-event-title-wrapper">
          {calendarEvent?.priority && (
            <span className="fc-event-priority-icon">
              {getPriorityIcon(calendarEvent.priority)}
            </span>
          )}
          <span className="fc-event-title">{event.title}</span>
        </div>
        {eventInfo.timeText && (
          <div className="fc-event-time">{eventInfo.timeText}</div>
        )}
      </div>
    );
  }, []);

  // Convert our CalendarView to FullCalendar view
  const calendarViewToFcView = useCallback((view: CalendarView): string => {
    switch (view) {
      case 'month': return 'dayGridMonth';
      case 'week': return 'timeGridWeek';
      case 'workWeek': return 'timeGridWeek';
      case 'day': return 'timeGridDay';
      case 'agenda': return 'listWeek';
      default: return 'dayGridMonth';
    }
  }, []);

  const firstDay = useMemo(() => {
    if (calendarPrefs.weekStart === 'monday') return 1;
    if (calendarPrefs.weekStart === 'sunday') return 0;
    return undefined;
  }, [calendarPrefs.weekStart]);

  const slotMinTime = calendarPrefs.workingHoursMode === 'custom'
    ? calendarPrefs.workingHoursStart
    : undefined;
  const slotMaxTime = calendarPrefs.workingHoursMode === 'custom'
    ? calendarPrefs.workingHoursEnd
    : undefined;

  const hiddenDays = useMemo(() => {
    if (currentView === 'workWeek' || !calendarPrefs.showWeekends) {
      return [0, 6];
    }
    return [];
  }, [calendarPrefs.showWeekends, currentView]);

  const effectiveSlotMinTime = slotMinTime ?? '00:00:00';
  const effectiveSlotMaxTime = slotMaxTime ?? '23:59:59';
  const effectiveScrollTime = slotMinTime ?? '06:00:00';

  // Handle event clicks
  const handleEventClick = useCallback((clickInfo: any) => {
    const calendarEvent = clickInfo.event.extendedProps.calendarEvent;
    if (calendarEvent) {
      setSelectedEventId(calendarEvent.id);
      onEventClick?.(calendarEvent);
    }
  }, [setSelectedEventId, onEventClick]);

  // Handle date/time selection
  const handleSelect = useCallback((selectInfo: any) => {
    onSlotSelect?.({
      start: selectInfo.start,
      end: selectInfo.end,
      slots: [selectInfo.start],
    });
  }, [onSlotSelect]);

  // Handle event drag and drop
  const handleEventDrop = useCallback((dropInfo: any) => {
    const calendarEvent = dropInfo.event.extendedProps.calendarEvent;
    if (calendarEvent) {
      moveEvent(calendarEvent.id, dropInfo.event.start, dropInfo.event.end);
    }
  }, [moveEvent]);

  // Handle event resize
  const handleEventResize = useCallback((resizeInfo: any) => {
    const calendarEvent = resizeInfo.event.extendedProps.calendarEvent;
    if (calendarEvent) {
      updateEvent(calendarEvent.id, {
        start: resizeInfo.event.start,
        end: resizeInfo.event.end,
      });
    }
  }, [updateEvent]);

  // Force FullCalendar to update when view changes
  useEffect(() => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      const targetView = calendarViewToFcView(currentView);
      console.log(`🔄 [FullCalendarView] Changing FullCalendar view to ${targetView} (from ${currentView})`);
      calendarApi.changeView(targetView);
    }
  }, [currentView, calendarViewToFcView]);

  // Force FullCalendar to update when date changes
  useEffect(() => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      console.log(`📅 [FullCalendarView] Changing FullCalendar date to ${currentDate.toISOString()}`);
      calendarApi.gotoDate(currentDate);
    }
  }, [currentDate]);

  // Dynamic headerToolbar based on sidebar state
  const getHeaderToolbar = useCallback(() => {
    if (isCalendarSidebarOpen) {
      // Sidebar open - normal layout
      return {
        left: 'prev,next today',
        center: 'title',
        right: 'dayGridMonth,timeGridWeek,timeGridDay'
      };
    } else {
      // Sidebar closed - move navigation to right to avoid floating menu button
      return {
        left: '',
        center: 'title', 
        right: 'prev,next today dayGridMonth,timeGridWeek,timeGridDay'
      };
    }
  }, [isCalendarSidebarOpen]);

  return (
    <div className={containerMode ? "fullcalendar-wrapper w-full h-full overflow-hidden" : "fullcalendar-wrapper w-full h-full overflow-hidden"}>
      <FullCalendar
        ref={calendarRef}
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        initialView={calendarViewToFcView(currentView)}
        initialDate={currentDate}
        events={fcEvents}
        weekends={calendarPrefs.showWeekends}
        firstDay={firstDay}
        
        // Event rendering
        eventContent={renderEventContent}
        
        // Interactions
        selectable={true}
        editable={true}
        
        // Event handlers
        eventClick={handleEventClick}
        select={handleSelect}
        eventDrop={handleEventDrop}
        eventResize={handleEventResize}
        
        // Dynamic header layout based on sidebar state
        headerToolbar={getHeaderToolbar()}
        
        // Styling - Adaptive height
        height="100%"
        contentHeight="100%"
        dayMaxEvents={true}
        
        // Time display - EXPANDED for debugging
        slotMinTime={effectiveSlotMinTime}  // Show full day by default, or user working hours
        slotMaxTime={effectiveSlotMaxTime}  // Show full day by default, or user working hours
        slotDuration="00:30:00"
        
        // Week options
        hiddenDays={hiddenDays}
        
        // Advanced options
        nowIndicator={true}
        scrollTime={effectiveScrollTime}  // Start scroll at working hours if set
        
        // Add debugging callbacks
        eventDidMount={(info) => {
          console.log(`🎯 [FullCalendar] Event mounted in ${currentView} view:`, {
            id: info.event.id,
            title: info.event.title,
            start: info.event.start?.toISOString(),
            end: info.event.end?.toISOString(),
            allDay: info.event.allDay,
            display: info.event.display,
            view: currentView,
          });
        }}
        
        eventsSet={(events) => {
          console.log(`📊 [FullCalendar] Events set for ${currentView} view:`, {
            totalEvents: events.length,
            currentView,
            events: events.map(e => ({
              id: e.id,
              title: e.title,
              start: e.start?.toISOString(),
              end: e.end?.toISOString(),
              allDay: e.allDay,
            }))
          });
        }}
        
        // Handle view/date changes with guards to prevent infinite loops
        datesSet={(dateInfo: any) => {
          console.log(`📅 [FullCalendar] datesSet called for ${currentView} view:`, {
            start: dateInfo.start.toISOString(),
            end: dateInfo.end.toISOString(),
            currentDate: currentDate.toISOString(),
            view: dateInfo.view.type,
            currentView,
          });
          
          // DISABLED: This was interfering with header navigation
          // The header navigation (navigateNext/navigatePrevious) should control the date
          // FullCalendar should follow, not lead
        }}
        
        viewDidMount={(viewInfo: any) => {
          console.log(`👁️ [FullCalendar] viewDidMount called:`, {
            viewType: viewInfo.view.type,
            currentView,
            title: viewInfo.view.title,
            start: viewInfo.view.currentStart?.toISOString(),
            end: viewInfo.view.currentEnd?.toISOString(),
          });
          
          const fcViewToCalendarView = (fcView: string): CalendarView => {
            switch (fcView) {
              case 'dayGridMonth': return 'month';
              case 'timeGridWeek': return 'week';
              case 'timeGridDay': return 'day';
              default: return 'month';
            }
          };
          const newView = fcViewToCalendarView(viewInfo.view.type);
          // Only update if the view actually changed to prevent infinite loop
          if (currentView !== newView) {
            console.log('👁️ [FullCalendarView] View changed, updating store:', newView);
            setCurrentView(newView);
          }
        }}
      />
    </div>
  );
};

export default FullCalendarView; 
